import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { countryModel } from 'entities/country';
import { UsersFilters, UsersTable, userModel } from 'entities/user';

import { EUserOnboardingStage, EUserRole } from 'shared/api';
import { routesApp } from 'shared/config';
import { useDebouncedSearch } from 'shared/lib';
import { LoadingProgress } from 'shared/ui';

export const ActiveUsersTab = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Получаем параметры из URL или используем значения по умолчанию
  const [filter, setFilter] = useState(searchParams.get('filter') ?? '');
  const [role, setRole] = useState<EUserRole | ''>((searchParams.get('role') as EUserRole) ?? '');
  const [country, setCountry] = useState(searchParams.get('country') ?? '');
  const [language, setLanguage] = useState(searchParams.get('language') ?? '');
  const [stage, setStage] = useState<EUserOnboardingStage | ''>(() => {
    const value = searchParams.get('stage') as EUserOnboardingStage;
    // Мусорное значение из URL не пускаем ни в API, ни в MUI Select
    return Object.values(EUserOnboardingStage).includes(value) ? value : '';
  });
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') ?? 'email');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') ?? 'asc',
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') ?? '25', 10));

  // Используем debounce для фильтра чтобы не запускать запрос при каждом символе
  const debouncedFilter = useDebouncedSearch(filter, 250);

  // Создаем объект параметров для API с использованием useMemo
  const params = useMemo(
    () => ({
      filter: debouncedFilter || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
      role: role || undefined,
      country: country || undefined,
      language: language || undefined,
      stage: stage || undefined,
    }),
    [debouncedFilter, sortBy, sortOrder, page, rowsPerPage, role, country, language, stage],
  );

  const { isLoading, isFetching } = userModel.getUsersAsync(params)(dispatch);
  const { userList, pagination } = userModel.useUser();
  const { data: countriesResponse } = countryModel.useAllCountries();
  const countries = countriesResponse?.data ?? [];

  // Обновляем URL при изменении параметров (используем debouncedFilter для URL)
  useEffect(() => {
    const newParams: Record<string, string> = {
      page: page.toString(),
      limit: rowsPerPage.toString(),
      sortBy,
      sortOrder,
    };

    if (debouncedFilter) newParams.filter = debouncedFilter;
    if (role) newParams.role = role;
    if (country) newParams.country = country;
    if (language) newParams.language = language;
    if (stage) newParams.stage = stage;

    setSearchParams(newParams, { replace: true });
  }, [debouncedFilter, role, country, language, stage, sortBy, sortOrder, page, rowsPerPage, setSearchParams]);

  // Мемоизируем обработчики с помощью useCallback
  const handleOpenUser = useCallback((userId: string) => navigate(`${routesApp.users}/${userId}`), [navigate]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setFilter(value);
    setPage(1);
  }, []);

  const handleRoleChange = useCallback((value: EUserRole | '') => {
    setRole(value);
    setPage(1);
  }, []);

  const handleCountryChange = useCallback((value: string) => {
    setCountry(value);
    setPage(1);
  }, []);

  const handleLanguageChange = useCallback((value: string) => {
    setLanguage(value);
    setPage(1);
  }, []);

  const handleStageChange = useCallback((value: EUserOnboardingStage | '') => {
    setStage(value);
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  // Показываем LoadingProgress только при первой загрузке (когда еще нет данных)
  if (isLoading && !userList.length) return <LoadingProgress />;

  return (
    <Box display="flex" flexDirection="column" px={3} py={2}>
      <UsersFilters
        filter={filter}
        role={role}
        country={country}
        language={language}
        stage={stage}
        countries={countries}
        totalCount={pagination?.total ?? 0}
        onFilterChange={handleFilterChange}
        onRoleChange={handleRoleChange}
        onCountryChange={handleCountryChange}
        onLanguageChange={handleLanguageChange}
        onStageChange={handleStageChange}
      />

      <UsersTable
        users={userList || []}
        openUser={handleOpenUser}
        pagination={pagination}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isFetching}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
      />
    </Box>
  );
};
